<script setup>
import {defineModel, defineProps, defineEmits, computed, ref, onUnmounted} from 'vue'
import GoogleSheetsBrandingSvg from "@/components/UI/svg/GoogleSheetsBrandingSvg.vue";
import GoogleSheetsDestinationSvg from "@/components/UI/svg/GoogleSheetsDestinationSvg.vue";
import TableSelect from "@/components/Connection/FileSelect.vue";
import PlusSvg from "@/components/UI/svg/PlusSvg.vue";
import CrossCloseNotifySvg from "@/components/UI/svg/CrossCloseNotifySvg.vue";
import {isStringEmpty} from "@/service/utils";
import GoogleDriveBrandingSvg from "@/components/UI/svg/GoogleDriveBrandingSvg.vue";
import {Api} from "@/service/api";
import LoadingSvg from "@/components/UI/LoadingSvg.vue";


const processorTypeToName = {
    'SOURCE': 'Источник',
    'DESTINATION': 'Назначение',
}

const props = defineProps({
    processorType: {
        type: String,
        required: true,
        validator: (value) => {
            return ['SOURCE', 'DESTINATION'].includes(value)
        }
    }
})

const files = defineModel('files')

const checkInterval = ref(null)

const emits = defineEmits(['reload-tabs', 'open-modal', 'add-source', 'change-step', 'next-step', 'save-files'])

const isAccountAttached = defineModel('isAccountAttached')

const isGoogleAccountLoading = ref(false)


const isTableSelected = computed(() => {
    for (const file of files.value) {
        if (isStringEmpty(file.id) || isStringEmpty(file.tab.sheetId)) {
            return false
        }
    }
    return true
})

function openModal() {
    emits('open-modal')
}

function createSourceDataProcessor() {
    emits('add-source')
}

function reloadTabs(spreadsheet_id, tab) {
    emits("reload-tabs", spreadsheet_id, tab)
}

function deleteItem(id) {
    const index = files.value.findIndex(file => file.id === id);
    if (index !== -1) {
        files.value.splice(index, 1);
    }

    if (files.value.length === 0) {
        createSourceDataProcessor()
    }
}

function changeStep() {
    emits('change-step');
}

function nextStep() {
    emits('next-step')
}

function saveFiles(files, index) {
    console.log("FILES", files, index)
    emits('save-files', files[0], index)
}

onUnmounted(() => {
    if (checkInterval.value) {
        clearInterval(checkInterval.value)
    }
})

async function attachGoogleAccount() {
    isGoogleAccountLoading.value = true
    const url = await Api.getGoogleOauthUrl()


    window.after_authorization = async function() {
        checkInterval.value = setInterval(async () => {
            try {
                await Api.getUserGoogleAccessToken()
                isGoogleAccountLoading.value = false
                clearInterval(checkInterval.value)
                isAccountAttached.value = true
            }
            catch (e) {
                if (e.response.status === 403) {
                    isAccountAttached.value = false
                }
            }
        }, 500)
    }

    window.open(url, '_blank', "top=100, left=100, width=400, height=500")

}

</script>

<template>
    <div class="relative">
        <div
            class="w-full flex flex-col justify-center items-start bg-white rounded-3xl shadow-xl"
        >
            <div class="flex items-center justify-between w-full">
                <div class="flex items-center justify-center h-32">
                    <GoogleSheetsBrandingSvg v-if="props.processorType === 'SOURCE'" class="w-20 ml-5 mr-2" />
                    <GoogleSheetsDestinationSvg v-if="props.processorType === 'DESTINATION'" class="w-20 ml-5 mr-2" />
                    <div>
                        <div class="font-bold text-xl">
                            {{ processorTypeToName[props.processorType] }}
                        </div>
                        <div>
                            Google Sheets
                        </div>
                    </div>
                </div>
                <div
                    class="mr-10 size-10 flex items-center justify-center rounded-full hover:bg-gray-200 cursor-pointer transition-all duration-150"
                    v-on:click="changeStep"
                >
                    <CrossCloseNotifySvg class="size-8"/>
                </div>
            </div>
            <div
                v-show="isAccountAttached"
                class="w-full flex flex-col items-center mb-10 border-t border-gray-200"
            >
                <TableSelect
                    v-for="(file, index) in files"
                    v-bind:key="file"
                    v-model:file="files[index]"
                    :index="index"
                    @reload-tabs="reloadTabs"
                    @open-modal="openModal"
                    @delete-item="deleteItem"
                    @saveFiles="saveFiles"
                />
                <div
                    v-on:click="createSourceDataProcessor"
                    class="flex items-center justify-center gap-2 rounded-md hover:bg-blue-100 w-4/5 h-20 cursor-pointer my-5 duration-150"
                >
                    <PlusSvg class="stroke-blue-600 size-5 stroke-[3px]" />
                    <div
                        class="font-bold text-blue-500"
                    >
                        Добавить источник данных
                    </div>
                </div>
            </div>
            <div v-show="!isAccountAttached" class="w-full flex flex-col items-center mb-10 border-t border-b border-gray-200">
                <div class="w-4/5 border-blue-500 border bg-blue-50 border-dashed rounded-lg min-h-fit max-h-fit my-10 flex-col items-center justify-center">
                    <GoogleDriveBrandingSvg class="size-10 mx-auto my-5" />
                    <div class="text-base mx-auto max-w-fit min-w-fit text-gray-500 my-3">
                        Выберите файлы из гугл диска
                    </div>
                    <div
                        v-on:click="attachGoogleAccount"
                        v-if="!isGoogleAccountLoading"
                        class="text-sm mx-auto max-w-fit min-w-fit font-bold text-white py-3 rounded-full bg-blue-500 px-3 mb-5 select-none cursor-pointer hover:bg-blue-600 duration-150 transition-all"
                    >
                        Подключите Google Drive
                    </div>
                    <div
                        v-else
                        class="text-sm mx-auto max-w-fit min-w-fit font-bold text-white py-3 rounded-full bg-blue-500 px-3 mb-5 select-none cursor-pointer hover:bg-blue-600 duration-150 transition-all"
                    >
                        <LoadingSvg class="size-6 animate-spin"/>
                    </div>
                </div>
            </div>
        </div>
        <div
            class="fixed left-1/2 -translate-x-1/2 sm:left-auto sm:right-4 md:right-6 lg:right-10 bottom-4 sm:bottom-5 px-4 sm:px-5 py-2.5 sm:py-3 rounded-full bg-blue-500 hover:bg-blue-600 active:bg-blue-700 duration-150 transition-all cursor-pointer select-none font-bold text-white z-30 shadow-lg hover:shadow-xl text-sm sm:text-base whitespace-nowrap"
            v-on:click="nextStep"
            v-if="isTableSelected"
        >
                Следующий шаг
        </div>
        <div
            class="fixed left-1/2 -translate-x-1/2 sm:left-auto sm:right-4 md:right-6 lg:right-10 bottom-4 sm:bottom-5 px-4 sm:px-5 py-2.5 sm:py-3 rounded-full cursor-default select-none bg-gray-400 font-bold text-gray-600 duration-150 transition-all z-30 shadow-lg hover:shadow-xl text-sm sm:text-base whitespace-nowrap"
            v-else
        >
            Следующий шаг
        </div>

    </div>

</template>

<style scoped>

</style>